import kali from 'kali';

import {
	getSession,
} from 'Vulture/Session.js';

import API from 'Vulture/lib/API.js';

import {
	broncoURL,
} from './ENV';

const writeErrorDetail = (err) => {
	const body = err && err.body;
	const serverDetail = body && typeof body.error === 'string' ? body.error.trim() : '';
	if (serverDetail) {
		return serverDetail;
	}
	if (err && err.status) {
		return `The server rejected the request (HTTP ${err.status}).`;
	}

	return (err && err.message) || 'Unknown error.';
};

export const fetchTemplateData = async (form) => {
	const url = `${broncoURL}/template_category`;
	const sessionKey = getSession().session;

	let retVal;

	await new kali({
		headers: {
			'content_type':   'application/json',
			'X-Auth-Session': sessionKey,
		},
		method: 'POST',
	}).post(url, {
		success: (_kali, res, data) => {
			if (data) {
				retVal = data;
			}
		},

		failure: (_kali, res, err) => {
			console.error(err);

			if (res.status === 503) {
				form.props.on503(true);
			}
		},
	});

	return retVal;
};

export const fetchSettingsForTemplateTheme = async (form) => {
	const url = `${broncoURL}/r/list/setting`;
	const sessionKey = getSession().session;

	let retVal;

	await new kali({
		body: {
			'skip_auth_filter': true,
			'filters':          [
				[
					{
						'key':       'name',
						'condition': 'IN',
						'val':       'horizontal',
						'operator':  'OR',
					},
					{
						'key':       'name',
						'condition': 'IN',
						'val':       'vertical',
						'operator':  'OR',
					},
					{
						'key':       'name',
						'condition': 'IN',
						'val':       'horizontal_4k',
						'operator':  'OR',
					},
					{
						'key':       'name',
						'condition': 'IN',
						'val':       'vertical_4k',
						'operator':  'OR',
					},
					{
						'key':       'name',
						'condition': 'IN',
						'val':       'is_custom',
						'operator':  'OR',
					},
					{
						'key':       'name',
						'condition': 'IN',
						'val':       'notes',
						'operator':  'AND',
					},
				],
			],
		},
		headers: {
			'content_type':   'application/json',
			'X-Auth-Session': sessionKey,
		},
		method: 'POST',
	}).post(url, {
		success: (_kali, res, contents) => {
			if (contents?.data) {
				retVal = contents.data;
			}
		},

		failure: (_kali, res, err) => {
			console.error(err);

			if (res.status === 503) {
				form.props.on503(true);
			}
		},
	});

	return retVal;
};

export const saveThemeActions = async (actions) => {
	if (!actions || actions.length === 0) {
		return {};
	}

	try {
		return await API.batch(actions);
	} catch (err) {
		console.error(err);

		return {
			'error':   true,
			'message': writeErrorDetail(err),
			'status':  err && err.status,
		};
	}
};

export const settingActionsFromPendingUpdates = (pendingUpdates) => {
	const actions = [];
	const byPair = {};

	Object.entries(pendingUpdates || {}).forEach(([key, val]) => {
		const segs = key.split('.');

		if (segs.length !== 5 || segs.some((seg) => seg === '')) {
			throw new Error(`Unrecognized pending update key: ${key}`);
		}

		const [m1, m1UUID, m2, m2UUID, attr] = segs;
		const pairKey = `${m1}.${m1UUID}.${m2}.${m2UUID}`;

		if (!byPair[pairKey]) {
			byPair[pairKey] = {
				'op':      'update_map',
				m1,
				'uuid':    m1UUID,
				m2,
				'm2_uuid': m2UUID,
				'body':    {},
			};

			actions.push(byPair[pairKey]);
		}

		byPair[pairKey].body[attr] = val;
	});

	return actions;
};

export const themeSettingSeedActions = (m1UUID, settingList) => (settingList || []).map((setting) => ({
	'op':      'update_map',
	'm1':      'template_theme',
	'uuid':    m1UUID,
	'm2':      'setting',
	'm2_uuid': setting.uuid,
	'body':    setting.name === 'notes' ? {
		'text': '',
	} : {
		'bool': false,
	},
}));

export const themeEntityActions = ({
	m1,
	m1UUID,
	displayName,
	isNew,
	parentM1,
	parentUUID,
	settingList,
}) => {
	const actions = [];

	if (isNew) {
		actions.push({
			'op':   'create',
			m1,
			'body': {
				'uuid':         m1UUID,
				'display_name': displayName,
			},
		});
	} else {
		actions.push({
			'op':   'update',
			m1,
			'uuid': m1UUID,
			'body': {
				'display_name': displayName,
			},
		});
	}

	if (parentM1 && parentUUID) {
		actions.push({
			'op':      'create_map',
			'm1':      parentM1,
			'uuid':    parentUUID,
			'm2':      m1,
			'm2_uuid': m1UUID,
		});

		if (m1 === 'template_theme') {
			actions.push(...themeSettingSeedActions(m1UUID, settingList));
		}
	}

	return actions;
};

export const submitPendingUpdates = async (pendingUpdates, form) => {
	let actions;

	try {
		actions = settingActionsFromPendingUpdates(pendingUpdates);
	} catch (err) {
		console.error(err);

		return {
			'error':   true,
			'message': err.message,
		};
	}

	const res = await saveThemeActions(actions);

	if (res.error && res.status === 503 && form && form.props) {
		form.props.on503(true);
	}

	return res;
};

export const handleDelete = async (body, form) => {
	const url = `${broncoURL}/delete/template_entity`;
	const sessionKey = getSession().session;

	let retVal;

	await new kali({
		body,
		headers: {
			'content_type':   'application/json',
			'X-Auth-Session': sessionKey,
		},
		method: 'POST',
	}).post(url, {
		success: (_kali, res, contents) => {
			// kali doesn't display messages from server errors
			retVal = contents;
		},

		failure: (_kali, res, err) => {
			console.error(err);
			alert(`Error Deleting: ${err}`);

			if (res.status === 503) {
				form.props.on503(true);
			}
		},
	});

	return retVal;
};